import React, { useContext } from 'react';
import styled from 'styled-components';

import { CollapsibleContentCard, SmallBody } from '@indigo-ag/component-module';
import { AgFinancingCreditApprovalStatus, CreditApprovalData, OpportunityType } from '@indigo-ag/schema';

import i18next from 'core/i18n';
import { Comments } from 'features/brazil-grower/components/DecisionSheet/Comments';
import { ConsultationsCard } from 'features/brazil-grower/components/DecisionSheet/Consultations/ConsultationsCard';
import { CreditApproval } from 'features/brazil-grower/components/DecisionSheet/CreditApproval/CreditApproval';
import { CreditApprovalHistory } from 'features/brazil-grower/components/DecisionSheet/CreditApprovalHistory';
import { CreditRiskAssessmentCard } from 'features/brazil-grower/components/DecisionSheet/CreditRiskAssessmentCard';
import { CropCashFlowList } from 'features/brazil-grower/components/DecisionSheet/CropCashFlowList';
import { PatrimonyAssetValuation } from 'features/brazil-grower/components/DecisionSheet/PatrimonyAssetValuation';
import { RequestedCredit } from 'features/brazil-grower/components/DecisionSheet/RequestedCredit';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { LoadingIndicator, isPresent } from 'shared';
import { CreditApprovalStatus } from '../../components/DecisionSheet/CreditApprovalStatus/CreditApprovalStatus';
import { EditCreditExceptions } from '../../components/DecisionSheet/EditCreditExceptions';
import { EscalateCreditApprovalV2 } from '../../components/DecisionSheet/EscalateCreditApprovalV2';
import { ResetCreditApproval } from '../../components/DecisionSheet/ResetCreditApproval';
import { StartCreditApproval } from '../../components/DecisionSheet/StartCreditApproval';
import { SubmitApprovalDecision } from '../../components/DecisionSheet/SubmitApprovalDecision';
import { EditCreditConditionals } from '../../components/DecisionSheet/EditCreditConditionals';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

const DecisionSheetContainer = styled(CollapsibleContentCard)`
  margin-top: 16px;
`;

const DecisionSheetItemsContainer = styled.div`
  > div {
    margin-top: 16px;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

interface Props {
  creditApprovalData?: CreditApprovalData;
  loading?: boolean;
}

const ExceptionsAndConditionalsWarning = () => {
  return (
    <SmallBody style={{ margin: '1rem 0' }}>
      <FontAwesomeIcon icon={faExclamationCircle} /> {i18next.t('exceptionsAndConditionals-importedFrom-warning')}
    </SmallBody>
  );
};

export const DecisionSheet = ({ creditApprovalData, loading }: Props) => {
  const { agFinance, importedExceptions, importedConditionals, parentId } = useContext(FinanceProcessContext);

  const hasImportedConditionals = importedConditionals.length > 0;
  const hasImportedExceptions = importedExceptions.length > 0;

  const hasDeprecatedCreditApprovalFlowStarted =
    !creditApprovalData?.round &&
    agFinance?.phase2?.creditApprovalStatus &&
    agFinance?.phase2?.creditApprovalStatus !== AgFinancingCreditApprovalStatus.IN_PROGRESS;

  const titleText =
    agFinance?.opportunityType === OpportunityType.BARTER_GUARANTEE
      ? i18next.t('decisionSheetGuarantee')
      : i18next.t('decisionSheet');

  return (
    <DecisionSheetContainer
      id="decision-sheet"
      titleText={titleText}
      renderAfterTitle={() =>
        !hasDeprecatedCreditApprovalFlowStarted ? (
          <ActionButtonsContainer>
            <EditCreditConditionals round={creditApprovalData?.round} initialConditionals={importedConditionals} />
            <EditCreditExceptions round={creditApprovalData?.round} alreadyAddedExceptions={importedExceptions} />
            <ResetCreditApproval round={creditApprovalData?.round} />
            <EscalateCreditApprovalV2 round={creditApprovalData?.round} />
            <SubmitApprovalDecision creditApprovalData={creditApprovalData} />
          </ActionButtonsContainer>
        ) : null
      }
    >
      {loading ? (
        <LoadingIndicator />
      ) : isPresent(creditApprovalData) && creditApprovalData?.decisionSheet ? (
        <DecisionSheetItemsContainer>
          {hasDeprecatedCreditApprovalFlowStarted ? (
            <CreditApprovalStatus phase2={agFinance?.phase2} />
          ) : (
            <>
              <StartCreditApproval
                creditApprovalData={creditApprovalData}
                creditApprovalStatus={agFinance?.phase2?.creditApprovalStatus}
              />
              {!hasImportedConditionals || !hasImportedExceptions ? <ExceptionsAndConditionalsWarning /> : null}
              <CreditApproval
                round={creditApprovalData.round}
                additionalLimit={creditApprovalData?.decisionSheet?.additionalLimit}
                phase2={agFinance?.phase2}
                importedExceptions={importedExceptions}
                importedConditionals={importedConditionals}
                parentId={parentId}
              />
            </>
          )}
          {agFinance?.opportunityType !== OpportunityType.BARTER_GUARANTEE ? (
            <>
              <CreditRiskAssessmentCard {...creditApprovalData.decisionSheet} />
              <CreditApprovalHistory priorLoans={creditApprovalData.decisionSheet?.priorLoans} />
              <CropCashFlowList cropCashFlows={creditApprovalData.decisionSheet?.cropCashFlows} />
              <PatrimonyAssetValuation {...creditApprovalData.decisionSheet} />
              <ConsultationsCard {...creditApprovalData.decisionSheet} />
              <RequestedCredit {...creditApprovalData.decisionSheet} />
              <Comments
                comments={creditApprovalData.decisionSheet?.otherComments}
                positivePoints={creditApprovalData.decisionSheet?.positivePoints}
                attentionPoints={creditApprovalData.decisionSheet?.attentionPoints}
              />
            </>
          ) : null}
        </DecisionSheetItemsContainer>
      ) : (
        i18next.t('error')
      )}
    </DecisionSheetContainer>
  );
};
